import Link from 'components/Globals/Base/Link';
import { NextCustomImage } from 'components/Image';
import type { Ranking, Year } from 'interfaces/content/Ranking';
import YearList from 'components/RankingsList/RankingsListItem/YearList';
import { RANKING_V2_PAGE } from 'constants/testsIds/rankingV2Page';
import * as S from './CardItemStyles';

interface CardItemProps extends Ranking {
  className?: string;
  hasArrow?: boolean;
}

const CardItem = ({
  title,
  slug,
  years,
  image,
  imageAlt,
  buttonTitle,
  postId,
  className = '',
  trackingData,
  hasArrow = false,
  externalUrl,
  listTypeAlias,
}: CardItemProps) => {
  const yearsDesc = [...years].sort((aYear: Year, bYear: Year) => Number(bYear.year) - Number(aYear.year));
  const latestYear: Year = yearsDesc[0];
  const isExternal = listTypeAlias === 'external';

  const isFullUrl = (str: string): boolean => {
    const createUrl = (url: string): URL | null => {
      try {
        return new URL(url);
      } catch {
        return null;
      }
    };
    return createUrl(str) !== null;
  };

  let latestListHref: string;
  if (isExternal) {
    latestListHref = latestYear?.externalUrl || externalUrl || slug;
  } else if (latestYear?.year && !isFullUrl(slug)) {
    const slugWithTrailingSlash = slug.endsWith('/') ? slug : `${slug}/`;
    latestListHref = `${slugWithTrailingSlash}${latestYear.year}/`;
  } else {
    latestListHref = slug;
  }

  const mainHref = isExternal ? latestYear?.externalUrl || externalUrl || slug : slug;
  const yearsForList: Year[] = isExternal
    ? years.map((year) => ({
        ...year,
        permalink: year.externalUrl || year.permalink,
      }))
    : years;

  return (
    <S.Wrapper
      className={className}
      data-cy={RANKING_V2_PAGE.MORE_RANKINGS_ITEM}
    >
      <S.HeaderWrapper>
        <S.ImageContainer>
          {image && (
            <Link
              href={mainHref}
              dataCy={RANKING_V2_PAGE.MORE_RANKINGS_IMAGE_LINK}
              trackingData={
                trackingData && {
                  ...trackingData,
                }
              }
              ariaLabel={title}
            >
              <NextCustomImage
                className='image more-rankings-image'
                width='164'
                height='455'
                src={`${image.split('?')[0]}?w=700`}
                alt={imageAlt || title || 'Ranking image'}
                data-cy={RANKING_V2_PAGE.MORE_RANKINGS_IMAGE}
              />
            </Link>
          )}
        </S.ImageContainer>
        <S.TitleContainer>
          <Link
            href={mainHref}
            className='title'
            dataCy={RANKING_V2_PAGE.MORE_RANKINGS_TITLE}
            trackingData={
              trackingData && {
                ...trackingData,
              }
            }
          >
            {title}
          </Link>
          <Link
            className='button'
            type='tertiaryButton'
            size={{ default: 'large' }}
            href={latestListHref}
            hasRightArrow={hasArrow}
            dataCy={RANKING_V2_PAGE.MORE_RANKINGS_BUTTON}
            trackingData={
              trackingData && {
                ...trackingData,
              }
            }
          >
            {buttonTitle ?? `view ${latestYear?.year || 'in'} list`}
          </Link>
        </S.TitleContainer>
      </S.HeaderWrapper>
      <YearList
        years={yearsForList}
        listId={postId}
        className='years'
      />
    </S.Wrapper>
  );
};

export default CardItem;
